import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "layout/AppLayout";
import { LoginLayout } from "layout/LoginLayout";
import { MapLayout } from "layout/MapLayout";

import { DefaultAppPage } from "pages/DefaultAppPage";
import { InfoPage } from "pages/InfoPage";
import { IndicatorSelectionPage } from "pages/indicators/IndicatorSelectionPage";
import { LayersPage } from "pages/LayersPage";
import { SettingsPage } from "pages/SettingsPage";
import { LoginPage } from "pages/login/LoginPage";
import { PasswordForgotPage } from "pages/login/PasswordForgotPage";
import { PasswordForgotConfirmPage } from "pages/login/PasswordForgotConfirmPage";
import { LogoutPage } from "pages/login/LogoutPage";
import { RegisterPage } from "pages/login/RegisterPage";
import { RegisterConfirmPage } from "pages/login/RegisterConfirmPage";

import { ConfigSettingsGuard } from "components/guards/ConfigSettingsGuard";
import { OutletContextPassthroughGuard } from "components/guards/OutletContextPassthroughGuard";
import { IsAuthenticedGuard } from "components/guards/IsAuthenticedGuard";
import { PageLoader } from "components/page/loader/PageLoader";
import { RootLayout } from "components/layout/RootLayout";

const AdminRootPage = lazy(() => import("pages/AdminRootPage"));

export function RootPage() {
  const defaultConfig = window._env_.REACT_APP_DEFAULT_CONFIG;

  return (
    <RootLayout>
      <Routes>
        <Route
          path="*"
          element={<IsAuthenticedGuard loader={<PageLoader />} />}
        >
          <Route path=":config" element={<ConfigSettingsGuard />}>
            <Route path="app" element={<AppLayout />}>
              <Route path="layers/add" element={<IndicatorSelectionPage />} />
              <Route path="*" element={<MapLayout />}>
                <Route path="layers" element={<LayersPage />}></Route>
                <Route path="settings" element={<SettingsPage />}></Route>
                <Route path="information" element={<InfoPage />}></Route>
              </Route>
              <Route index element={<DefaultAppPage />} />
            </Route>
            <Route
              path="admin/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminRootPage />
                </Suspense>
              }
            />

            {defaultConfig && (
              <Route
                index
                element={<Navigate to={`/${defaultConfig}/app`} />}
              />
            )}
          </Route>
          <Route path="*" element={<ConfigSettingsGuard />} />
        </Route>
        <Route path="*" element={<LoginLayout />}>
          <Route
            path="password/forgot/confirm/:token"
            element={<PasswordForgotConfirmPage />}
          />
          <Route
            path="register/confirm/:token"
            element={<RegisterConfirmPage />}
          />
          <Route path=":config/app" element={<OutletContextPassthroughGuard />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="logout" element={<LogoutPage />} />
            <Route path="password/forgot" element={<PasswordForgotPage />} />
            <Route path="register" element={<RegisterPage />} />
          </Route>
          {defaultConfig && (
            <Route index element={<Navigate to={`/${defaultConfig}/app`} />} />
          )}
          <Route index element={<Navigate to={`layers`} />} />
        </Route>
        <Route element={<PageLoader />} />
      </Routes>
    </RootLayout>
  );
}
